import { template } from 'lodash'
import PropTypes from 'prop-types'
import React, { useContext } from 'react'

import Icon from 'components/Icon'
import { LocalizationContext } from 'contexts/Localization'
import { ResizeContext } from 'contexts/Resize'

import CartCostBlock from '../CartCostBlock'

import DeliveryDropdown from './components/DeliveryDropdown'
import DeliveryTooltip from './components/DeliveryTooltip/DeliveryTooltip'
import { CartItemLayoutStyled, Text, IconBlock, TextBlock } from './styles'

/**
 * CartDelivery
 *
 * @param {object} props - properties of component
 * @returns {node} CartDelivery
 */
const CartDelivery = ({
  cost,
  isFree,
  texts,
  isCartInitOrUpdate,
  isSinglePageCart,
  isDisabled,
  deliveryType,
  deliveryAvailableTypes,
  changeDeliveryType,
  precision,
}) => {
  const {
    currentCountry: { intlCode },
    sales: { currency, delivery: deliveryConfig },
    paths: { shared },
    dict,
  } = React.useContext(LocalizationContext)
  const { tablet, mobile } = useContext(ResizeContext)
  const layoutRef = React.useRef(null)
  const isDropdownForDelivery =
    deliveryAvailableTypes && deliveryAvailableTypes.length > 0

  const isAlwaysFreeDelivery = !cost && !deliveryConfig.cost

  const title = React.useMemo(() => {
    if (isSinglePageCart) return texts.delivery.row_title

    const compileTitle = template(texts.delivery.title)
    const placeholder = isCartInitOrUpdate && '...'
    return compileTitle({
      from: placeholder || deliveryConfig.from,
      to: placeholder || deliveryConfig.to,
    })
  }, [deliveryConfig, isSinglePageCart, texts.delivery, isCartInitOrUpdate])

  const dropdown = React.useMemo(
    () =>
      isDropdownForDelivery && !isAlwaysFreeDelivery ? (
        <DeliveryDropdown
          {...{
            deliveryType,
            deliveryAvailableTypes,
            changeDeliveryType,
            dict,
            texts,
            isDisabled,
            config: deliveryConfig,
          }}
        />
      ) : null,
    [
      isDropdownForDelivery,
      isAlwaysFreeDelivery,
      deliveryAvailableTypes,
      deliveryType,
      changeDeliveryType,
      dict,
      texts,
      deliveryConfig,
      isDisabled,
    ],
  )

  const description = React.useMemo(
    () =>
      isDropdownForDelivery || isAlwaysFreeDelivery ? null : (
        <TextBlock>
          <IconBlock>
            <Icon id={isFree ? 'color_done' : 'color_exclamation'} />
          </IconBlock>
          {texts.delivery.info ? (
            <DeliveryTooltip
              texts={texts}
              path={shared}
              tablet={tablet}
              mobile={mobile}
              leftIndentNode={layoutRef}
            />
          ) : (
            <Text
              dangerouslySetInnerHTML={{ __html: texts.delivery.content }}
            />
          )}
        </TextBlock>
      ),
    [
      isDropdownForDelivery,
      isAlwaysFreeDelivery,
      isFree,
      texts,
      shared,
      tablet,
      mobile,
    ],
  )

  const rightBlockContent = React.useMemo(
    () => (
      <CartCostBlock
        intlCode={intlCode}
        currencyCode={currency}
        cost={cost}
        precision={precision}
        realCost={isFree ? deliveryConfig.cost : cost}
        showCrossedCost={isFree && !isAlwaysFreeDelivery}
        isLoading={isCartInitOrUpdate}
      />
    ),
    [
      isCartInitOrUpdate,
      intlCode,
      currency,
      cost,
      isFree,
      deliveryConfig,
      precision,
      isAlwaysFreeDelivery,
    ],
  )

  return (
    <CartItemLayoutStyled
      layoutRef={layoutRef}
      alignByCenter={false}
      title={title}
      containerClassName="cart-delivery__wrapper"
      descrBlockContent={dropdown || description}
      rightBlockContent={rightBlockContent}
    />
  )
}

CartDelivery.propTypes = {
  /** Cost of delivery */
  cost: PropTypes.number.isRequired,

  /** Cost of delivery is free because of stock condition */
  isFree: PropTypes.bool.isRequired,

  /** Information about goods item */
  texts: PropTypes.object.isRequired,

  /** Is block disabled */
  isDisabled: PropTypes.bool.isRequired,

  /** Hide cost block if cart init or update */
  isCartInitOrUpdate: PropTypes.bool,

  /** Show delivery time if cart is not single page */
  isSinglePageCart: PropTypes.bool,

  /** Change delivery type */
  changeDeliveryType: PropTypes.func,

  /** Current delivery type */
  deliveryType: PropTypes.number,

  /** Precision of cost */
  precision: PropTypes.number,

  /** Available delivery types for locale */
  deliveryAvailableTypes: PropTypes.array,
}

export default CartDelivery
